import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildRiskInput,
  UpdateRiskMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

/**
 * The wrapper's public input. `OriginalTimestamp` is the optimistic-concurrency
 * token (COI-622) — the risk row's last-seen `ModifiedAtTimestamp`. The legacy
 * `updateChildRisk` GraphQL input has no such field, so it is stripped on the
 * Apollo leg and only the tRPC path locks on it.
 */
type UpdateRiskInput = UpdateChildRiskInput & {
  OriginalTimestamp?: string | null;
};

type UpdateRiskTRPCInput = Omit<UpdateRiskInput, 'schedule'> &
  ScheduleTRPCInput;

export const useUpdateRisk = createMutationHook({
  name: 'updateRisk',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.risk.update.mutationOptions({ onSuccess }),
  mapInput: ({ schedule, ...rest }: UpdateRiskInput): UpdateRiskTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
  }),
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.risk.riskById.queryKey(),
    trpc.frontend.risk.scores.queryKey(),
    trpc.frontend.risk.riskListOnlyOptimized.queryKey(),
    trpc.frontend.risk.riskListOnlyWithEntitiesOptimized.queryKey(),
    trpc.frontend.risk.riskScoresByRiskId.queryKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateRiskInput
  ): UpdateRiskMutation => ({
    updateChildRisk: {
      __typename: 'IdOutput',
      Id: unwrapApprovalGated(result, { Id: input.Id }).Id,
    },
  }),
  graphqlDocument: UpdateRiskDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateRiskInput) => ({
    object: input,
  }),
  evictFields: ['risk', 'risk_score'],
  refetchQueries: [
    namedOperations.Query.getRiskById,
    namedOperations.Query.getRisksByTier,
  ],
});
